import React, { useEffect, useState, useCallback, useContext, useMemo, useRef } from "react";
import { GoogleMap, MarkerF, OverlayViewF, OVERLAY_MOUSE_TARGET, useJsApiLoader } from "@react-google-maps/api";
import CustomMapMarker from "./components/CustomMapMarker";
import EventMapMarker from "./components/EventMapMarker";
import CategoryMultiSelect from "./components/CategoryMultiSelect";
import ToggleBar from "./components/ToggleBar";
import SearchResult from "./components/SearchResult";
import SearchResultEvent from "./components/SearchResultEvent";
import ContactForm from "./components/ContactForm";
import EventForm from "./components/EventForm";
import { MapStylesGreen } from './components/MapStyles'
import { RemoteDataContext } from './providers/RemoteDataProvider';
import ContactFormScreenOverlay from "./components/ContactFormScreenOverlay";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const SUBMIT_ENDPOINT = process.env.REACT_APP_SUBMIT_ENDPOINT;
const EVENT_FORM_SUBMIT_ENDPOINT = process.env.REACT_APP_EVENT_FORM_SUBMIT_ENDPOINT;

const libraries = ["places"];
const mapContainerStyle = {
  width: "100vw",
  height: "100vh",
  maxWidth: "100%"
};

const defaultCenter = {
  lat: 51.165691,
  lng: 10.451526
};

const defaultZoom = 6;

// Orientation marker for the user's position resp. the searched address. Apple Maps
// style: small dot with a white outline plus a soft, pulsing halo - about half the size
// of the Apple original so it does not dominate the partner markers.
const userPositionIcon = () => {
  // The canvas is deliberately larger than the dot so the halo has room to expand.
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="46" height="46" viewBox="0 0 46 46">
    <style>
      .halo { animation: userDotPulse 2s ease-out infinite; transform-origin: 23px 23px; }
      .halo-delayed { animation-delay: 1s; }
      @keyframes userDotPulse {
        0% { transform: scale(.35); opacity: .5; }
        100% { transform: scale(1); opacity: 0; }
      }
    </style>
    <circle class="halo" cx="23" cy="23" r="21" fill="#5CC244"/>
    <circle class="halo halo-delayed" cx="23" cy="23" r="21" fill="#5CC244"/>
    <circle cx="23" cy="23" r="6.5" fill="#5CC244" stroke="#ffffff" stroke-width="3"/>
  </svg>`;

  return {
    url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
    scaledSize: new window.google.maps.Size(46, 46),
    // Centred: the dot marks the exact position, unlike a teardrop pin.
    anchor: new window.google.maps.Point(23, 23)
  };
};

const MapCluster = ({ cluster, kind, onActivate }) => {
  const buttonRef = useRef(null);
  const clusterRef = useRef(cluster);
  const onActivateRef = useRef(onActivate);
  const count = cluster.members.length;
  const size = count >= 50 ? 64 : count >= 10 ? 56 : 48;
  const borderWidth = count >= 50 ? 4 : 3;
  const label = count > 99 ? '99+' : String(count);
  const title = `${count} ${kind === 'event' ? 'Events' : 'Ergebnisse'} – zum Vergrößern klicken`;

  clusterRef.current = cluster;
  onActivateRef.current = onActivate;

  useEffect(() => {
    const button = buttonRef.current;
    if (!button) return undefined;

    let pointerStarted = false;

    const stopEvent = (event) => {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    };
    const handlePointerDown = (event) => {
      stopEvent(event);
      pointerStarted = true;
      button.classList.add('is-active');
    };
    const handlePointerUp = (event) => {
      stopEvent(event);
      button.classList.remove('is-active');
      if (!pointerStarted) return;
      pointerStarted = false;
      onActivateRef.current(clusterRef.current);
    };
    const handlePointerCancel = () => {
      pointerStarted = false;
      button.classList.remove('is-active');
    };
    const handlePointerEnter = () => button.classList.add('is-hovered');
    const handlePointerLeave = () => {
      button.classList.remove('is-hovered');
      handlePointerCancel();
    };
    const handleKeyboardClick = (event) => {
      if (event.detail !== 0) return;
      stopEvent(event);
      onActivateRef.current(clusterRef.current);
    };

    button.addEventListener('pointerdown', handlePointerDown, true);
    button.addEventListener('pointerup', handlePointerUp, true);
    button.addEventListener('pointercancel', handlePointerCancel, true);
    button.addEventListener('pointerenter', handlePointerEnter, true);
    button.addEventListener('pointerleave', handlePointerLeave, true);
    button.addEventListener('click', handleKeyboardClick, true);
    button.addEventListener('dblclick', stopEvent, true);

    return () => {
      button.removeEventListener('pointerdown', handlePointerDown, true);
      button.removeEventListener('pointerup', handlePointerUp, true);
      button.removeEventListener('pointercancel', handlePointerCancel, true);
      button.removeEventListener('pointerenter', handlePointerEnter, true);
      button.removeEventListener('pointerleave', handlePointerLeave, true);
      button.removeEventListener('click', handleKeyboardClick, true);
      button.removeEventListener('dblclick', stopEvent, true);
    };
  }, []);

  return (
    <OverlayViewF
      position={cluster.position}
      mapPaneName={OVERLAY_MOUSE_TARGET}
      getPixelPositionOffset={() => ({ x: -size / 2, y: -size / 2 })}>
      <button
        ref={buttonRef}
        type="button"
        className={`map-cluster map-cluster-${kind}`}
        style={{
          appearance: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: `${size}px`,
          height: `${size}px`,
          padding: 0,
          color: '#ffffff',
          background: '#09090b',
          border: `${borderWidth}px solid #5CC244`,
          borderRadius: '50%',
          boxShadow: '0 4px 12px rgba(0,0,0,0.45), inset 0 0 0 4px rgba(255,255,255,0.08)',
          fontSize: '16px',
          lineHeight: 1,
          fontWeight: 700,
          cursor: 'pointer',
          pointerEvents: 'auto',
          touchAction: 'manipulation',
          position: 'relative',
          zIndex: 1000
        }}
        aria-label={title}
        title={title}>
        <span>{label}</span>
      </button>
    </OverlayViewF>
  );
};

const getClusterGridSize = (zoom) => zoom <= 6 ? 160 : zoom <= 8 ? 110 : zoom <= 10 ? 70 : 40;

const clusterMapItems = (items, zoom, gridSize = getClusterGridSize(zoom), mergeNearbySingletons = false) => {
  const scale = 256 * Math.pow(2, zoom);
  const buckets = new Map();

  Object.values(items || {}).forEach((item) => {
    const lat = Number(item.lat);
    const lng = Number(item.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    const sinLat = Math.sin(lat * Math.PI / 180);
    const x = ((lng + 180) / 360) * scale;
    const y = (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * scale;
    const key = `${Math.floor(x / gridSize)}:${Math.floor(y / gridSize)}`;
    const bucket = buckets.get(key) || [];
    bucket.push(item);
    buckets.set(key, bucket);
  });

  const clusters = Array.from(buckets.values()).map((members) => ({
    members,
    position: {
      lat: members.reduce((sum, item) => sum + Number(item.lat), 0) / members.length,
      lng: members.reduce((sum, item) => sum + Number(item.lng), 0) / members.length
    }
  }));

  if (!mergeNearbySingletons) return clusters;

  // A fixed grid can leave a marker isolated just across a cell boundary even
  // when it visually belongs to a nearby cluster. Absorb only those singletons
  // into an existing cluster, leaving all established cluster groups unchanged.
  const mergeDistance = gridSize * 0.75;
  const populatedClusters = clusters.filter((cluster) => cluster.members.length > 1);
  const unmergedSingletons = [];

  clusters.filter((cluster) => cluster.members.length === 1).forEach((singleton) => {
    const singletonPoint = projectMapPoint(singleton.position, zoom);
    let nearestCluster = null;
    let nearestDistance = Number.POSITIVE_INFINITY;

    populatedClusters.forEach((cluster) => {
      const clusterPoint = projectMapPoint(cluster.position, zoom);
      const distance = Math.hypot(clusterPoint.x - singletonPoint.x, clusterPoint.y - singletonPoint.y);
      if (distance < nearestDistance) {
        nearestCluster = cluster;
        nearestDistance = distance;
      }
    });

    if (!nearestCluster || nearestDistance > mergeDistance) {
      unmergedSingletons.push(singleton);
      return;
    }

    nearestCluster.members = [...nearestCluster.members, singleton.members[0]];
    nearestCluster.position = {
      lat: nearestCluster.members.reduce((sum, item) => sum + Number(item.lat), 0) / nearestCluster.members.length,
      lng: nearestCluster.members.reduce((sum, item) => sum + Number(item.lng), 0) / nearestCluster.members.length
    };
  });

  return [...populatedClusters, ...unmergedSingletons];
};

const projectMapPoint = (position, zoom) => {
  const scale = 256 * Math.pow(2, zoom);
  const sinLat = Math.sin(Number(position.lat) * Math.PI / 180);
  return {
    x: ((Number(position.lng) + 180) / 360) * scale,
    y: (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * scale
  };
};

// only one toggle can be active at a time
// if toggle is active only show these items
const staticToggles = [
  {
    label: "Events",
    value: "showEvents",
    active: false
  },
  {
    label: "Showroom",
    value: "showShowroom",
    active: false
  }
];

const SEARCH_RESULT_TYPE = {
  DEFAULT: "default",
  EVENT: "event",
  SHOWROOM: "showroom"
};

// Filters that are pre-selected on a fresh start. Without a preselection, leads can be
// submitted without any filter preference at all.
const DEFAULT_ACTIVE_CATEGORY_VALUES = ["hasPvSpeicher"];

// Restores the default selection while keeping the category metadata (label, icon)
// that only exists in component state.
const withDefaultCategoryState = (categories) => categories.map((category) => ({
  ...category,
  active: DEFAULT_ACTIVE_CATEGORY_VALUES.includes(category.value)
}));

const withAllCategoriesDisabled = (categories) => categories.map((category) => ({
  ...category,
  active: false
}));

const MAP_DEEPLINK_QUERY_PARAM = "map_deeplink";

const base64DecodeUnicode = (base64) => {
  const normalized = base64.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(normalized.length + ((4 - normalized.length % 4) % 4), '=');
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, char => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
};

const parseE3dcMapConfigValue = (configValue) => {
  if (!configValue) return {};

  try {
    const trimmedValue = configValue.trim();
    const json = trimmedValue.startsWith('{') ? trimmedValue : base64DecodeUnicode(trimmedValue);
    const payload = JSON.parse(json);
    return payload && !Array.isArray(payload) && typeof payload === 'object' ? payload : {};
  } catch (error) {
    console.error('Invalid map_deeplink config', error);
    return {};
  }
};

const getPartnerIdsFromE3dcMapConfig = (config) => {
  if (!Array.isArray(config.partner_ids)) return [];

  return config.partner_ids.filter(Number.isInteger);
};

const getEventPartnerIdentifier = (event) => Number(event?.partnerId);

const parseE3dcMapConfig = (params) => {
  const configValue = params.get(MAP_DEEPLINK_QUERY_PARAM);

  return parseE3dcMapConfigValue(configValue);
};

const getUrlQueryParams = (url) => {
  if (!url) return new URLSearchParams();

  try {
    return new URL(url).searchParams;
  } catch (error) {
    return new URLSearchParams();
  }
};

const getHashQueryParams = () => {
  const hash = window.location.hash || '';
  const queryIndex = hash.indexOf('?');

  return queryIndex === -1 ? new URLSearchParams() : new URLSearchParams(hash.substring(queryIndex));
};

const getBrowserE3dcMapConfigs = () => [
  parseE3dcMapConfig(new URLSearchParams(window.location.search)),
  parseE3dcMapConfig(getHashQueryParams()),
  parseE3dcMapConfig(getUrlQueryParams(document.referrer))
];

const parseLockedPartnerIds = (embeddedDeeplink) => {
  const embeddedPartnerIds = getPartnerIdsFromE3dcMapConfig(parseE3dcMapConfigValue(embeddedDeeplink));
  if (embeddedPartnerIds.length > 0) {
    return embeddedPartnerIds;
  }

  for (const config of getBrowserE3dcMapConfigs()) {
    const partnerIds = getPartnerIdsFromE3dcMapConfig(config);
    if (partnerIds.length > 0) {
      return partnerIds;
    }
  }

  return [];
};

const App = ({ embeddedDeeplink }) => {

  const { locations, locationsById, events } = useContext(RemoteDataContext);
  const lockedPartnerIds = useMemo(() => parseLockedPartnerIds(embeddedDeeplink), [embeddedDeeplink]);
  const hasLockedPartnerFilter = lockedPartnerIds.length > 0;
  const lockedPartnerIdSet = useMemo(() => new Set(lockedPartnerIds), [lockedPartnerIds]);
  const lockedPartnerLocations = useMemo(
    () => lockedPartnerIds.map((partnerId) => locationsById[partnerId]).filter(Boolean),
    [lockedPartnerIds, locationsById]
  );
  const lockedPartnerEvents = useMemo(
    () => Object.values(events || {}).filter((event) => lockedPartnerIdSet.has(getEventPartnerIdentifier(event))),
    [events, lockedPartnerIdSet]
  );
  const [filteredLocations, setFilteredLocations] = useState([]);  
  const [toggles] = useState(staticToggles);
  const [selectedToggles, setSelectedToggles] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchString, setSearchString] = useState("");
  const [searchError, setSearchError] = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const suggestionSessionRef = useRef(0);
  const suppressSuggestionsRef = useRef(null);
  // Counts the executed searches. Predictions belonging to an older generation are
  // discarded, so a late response cannot reopen the dropdown after a search.
  const searchGenerationRef = useRef(0);
  // True while a search is running. The input is rewritten to the normalised name during
  // this time, which must not trigger new suggestions.
  const isSearchPendingRef = useRef(false);
  const [userPosition, setUserPosition] = useState(null);
  const [isLocating, setIsLocating] = useState(false);
  const [map, setMap] = useState(null);
  const [mapZoom, setMapZoom] = useState(defaultZoom);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [hasUserPannedMap, setHasUserPannedMap] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [showResultEvent, setShowResultEvent] = useState(false); // true: ignore if no location is given => for testing
  const [currentCenter, setCurrentCenter] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [filterCity, setFilterCity] = useState("");  
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  // B2C is preselected: it is the less restrictive mode and keeps the map usable right
  // away. Without a preselection direct marker clicks would have to be blocked, because
  // B2C and B2B show different partners.
  const [customerType, setCustomerType] = useState('B2C');
  const [searchResultType, setSearchResultType] = useState(SEARCH_RESULT_TYPE.DEFAULT);
  // Same initial value, otherwise the restore effect would treat the first render as a
  // customer type change.
  const previousCustomerTypeRef = useRef('B2C');
  const [previousCategoriesB2B, setPreviousCategoriesB2B] = useState([]);
  const [categories, setCategories] = useState([
    {
      label: "PV & Speicher",
      value: "hasPvSpeicher",
      active: true,
      svg: () => (<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 54.2 47"><path fill="currentColor" d="m51.2 8 3-8zM46 21.8h.1l3.4-9.2h-.1z"></path><path fill="currentColor" d="M32.8 21.8H46l3.4-9.2H36.3zM51.2 8l3-8h-13l-3.1 8zM27.9 21.8l3.5-9.2H19.7l-3.5 9.2zM33.2 8l3.1-8H24.6l-3.1 8zM16.6 8l3-8H8.2l-3 8zM14.8 12.6H3.5L0 21.8h11.3zM27.4 40V25.5h-4.6V40c-6.5.4-11.5 2.7-11.5 5.5v1.6h27.5v-1.6c0-2.8-5-5.1-11.5-5.5Z"></path></svg>)
    },
    {
      label: "E-Mobilität",
      value: "hasEMobility",
      active: false,
      svg: () => (<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 76 59"><path fill="none" d="M18.7 10H32V4h-2.9c-5.7 0-10.4 6-10.4 6M36 4v6h16.5S43.3 4 39 4z"></path><path fill="currentColor" d="M59.7 23c0 .3-.1.6-.1 1v1c.6 1.7 2.1 3 4 3s3.4-1.3 3.9-3c0-.3.1-.7.1-1 0-2.2-1.8-4-4-4s-3.4 1.3-3.9 3M19.7 20.2c-1.8 0-3.3 1.3-3.7 2.9 0 .3-.2.7-.2 1v.8c.4 1.8 1.9 3.1 3.8 3.2h.1c1.9 0 3.5-1.4 3.9-3.2v-.8c0-2.2-1.8-4-4-4h-.1Z"></path><path fill="currentColor" d="M74.9 16.9c-.3-.8-1.1-2.2-3.4-3.3-3-1.5-8.5-3.2-12.7-4.2-2.2-1.5-8-4.7-13.7-7.3-2-.9-5.1-2.1-7.9-2.1h-7.5c-7.8 0-17.8 9-17.8 9l-6 1v11.3c.4-.1.9-.2 1.3-.2l-1.3.9v-.7c-3.5.9-6 4-6 7.8V31c0 4.4 3.6 8 8 8.1h54.3c2.2 0 4.1 1.8 4.1 4.1v1.9c0 2.2-1.8 4.1-4.1 4.1H43.6v4h18.6c4.4 0 8-3.6 8-8.1v-1.9c0-4.4-3.6-8-8-8.1H8.1C5.9 35.1 4 33.3 4 31v-1.9C4 26.9 5.8 25 8.1 25h4.7v-.8c0-2 .8-3.7 2.1-5 1.2-1.2 2.9-2 4.8-2 3.8 0 6.9 3.2 6.9 7V26h30.2v-2c0-2.2 1-4.2 2.6-5.5.3-.2.5-.4.8-.6 1.4-.8 3.1-1.2 4.9-.8 3.5.7 5.8 3.9 5.6 7.4v1.4h4.2l1.4-3.9s-1-4.8-1.1-5.1ZM32 10H18.7s4.7-6 10.4-6H32zm4 0V4h3c4.3 0 13.5 6 13.5 6z"></path><path fill="currentColor" d="M37 43h-5v3h-6v3h6v4h-6v3h6v3h5.1c3.3-.4 5.9-2.8 6.7-6v-4c-.8-3.1-3.5-5.6-6.7-6ZM7.3 21c-.4 0-.9.1-1.3.2v.7z"></path></svg>)
    },
    {
      label: "Smart-Meter",
      value: "hasSmartMeter",
      active: false,
      svg: () => (<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 28" fill="none"><path d="M25.1481 3.27383V24.7321H2.85185V3.27383H25.1481ZM28 0H0V28H28V0Z" fill="currentColor" /><path d="M7.59376 8.519C7.23909 8.981 6.73742 9.212 6.08876 9.212C5.44476 9.212 4.94309 8.981 4.58376 8.519C4.22909 8.06166 4.05176 7.41533 4.05176 6.58C4.05176 5.768 4.22909 5.13566 4.58376 4.683C4.94309 4.23033 5.44476 4.004 6.08876 4.004C6.73276 4.004 7.23209 4.23033 7.58676 4.683C7.94609 5.13566 8.12576 5.768 8.12576 6.58C8.12576 7.41533 7.94842 8.06166 7.59376 8.519ZM5.10176 6.601C5.10176 7.75833 5.43076 8.337 6.08876 8.337C6.74676 8.337 7.07576 7.75833 7.07576 6.601C7.07576 5.453 6.74676 4.879 6.08876 4.879C5.43076 4.879 5.10176 5.453 5.10176 6.601Z" fill="currentColor" /><path d="M15.5049 8.519C15.1502 8.981 14.6485 9.212 13.9999 9.212C13.3559 9.212 12.8542 8.981 12.4949 8.519C12.1402 8.06166 11.9629 7.41533 11.9629 6.58C11.9629 5.768 12.1402 5.13566 12.4949 4.683C12.8542 4.23033 13.3559 4.004 13.9999 4.004C14.6439 4.004 15.1432 4.23033 15.4979 4.683C15.8572 5.13566 16.0369 5.768 16.0369 6.58C16.0369 7.41533 15.8595 8.06166 15.5049 8.519ZM13.0129 6.601C13.0129 7.75833 13.3419 8.337 13.9999 8.337C14.6579 8.337 14.9869 7.75833 14.9869 6.601C14.9869 5.453 14.6579 4.879 13.9999 4.879C13.3419 4.879 13.0129 5.453 13.0129 6.601Z" fill="currentColor" /><path d="M23.4159 8.519C23.0613 8.981 22.5596 9.212 21.9109 9.212C21.2669 9.212 20.7653 8.981 20.4059 8.519C20.0513 8.06166 19.8739 7.41533 19.8739 6.58C19.8739 5.768 20.0513 5.13566 20.4059 4.683C20.7653 4.23033 21.2669 4.004 21.9109 4.004C22.5549 4.004 23.0543 4.23033 23.4089 4.683C23.7683 5.13566 23.9479 5.768 23.9479 6.58C23.9479 7.41533 23.7706 8.06166 23.4159 8.519ZM20.9239 6.601C20.9239 7.75833 21.2529 8.337 21.9109 8.337C22.5689 8.337 22.8979 7.75833 22.8979 6.601C22.8979 5.453 22.5689 4.879 21.9109 4.879C21.2529 4.879 20.9239 5.453 20.9239 6.601Z" fill="currentColor" /><rect x="5" y="21.8" width="4.2" height="1.2" rx="0.6" fill="currentColor" /><circle cx="7.1" cy="19.1" r="1.5" stroke="currentColor" strokeWidth="1.2" /><rect x="12.2002" y="21.8" width="4.2" height="1.2" rx="0.6" fill="currentColor" /><circle cx="14.3002" cy="19.1" r="1.5" stroke="currentColor" strokeWidth="1.2" /><rect x="19.3994" y="21.8" width="4.2" height="1.2" rx="0.6" fill="currentColor" /><circle cx="21.4994" cy="19.1" r="1.5" stroke="currentColor" strokeWidth="1.2" /><rect x="2" y="10" width="24" height="1" fill="currentColor" /><rect x="9.5" y="11" width="9" height="1" transform="rotate(-90 9.5 11)" fill="currentColor" /><rect x="17.5" y="11" width="9" height="1" transform="rotate(-90 17.5 11)" fill="currentColor" /></svg>)
    },
    {
      label: "Wärmepumpe Installation",
      value: "hasWaermepumpeInstall",
      active: false,
      svg: () => (<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 69.2 55.28"><g fill="currentColor"><path d="M64.18 5.4v2.02h-7.43V0c-2.97 1.38-4.93 4.31-4.93 7.81 0 3.21 1.74 6.01 4.33 7.52v22.38c0 2.39 1.97 4.33 4.38 4.33s4.27-1.94 4.27-4.33V15.37a8.69 8.69 0 0 0 4.41-7.57c0-3.62-1.76-6.49-5.03-7.8zM61.7 37.7a1.24 1.24 0 1 1-2.48 0 1.24 1.24 0 0 1 2.48-.01Z"></path><path d="M48.5 22.08v27.7h-43v-27.7zm5.5-5.51H0v38.71h54z"></path><path d="M32.99 26.29h12.16v3.25H32.99zM32.99 32.1h12.16v3.25H32.99zM32.99 37.9h12.16v3.25H32.99zM19.21 24.87c-6.34 0-11.47 5.14-11.47 11.47s5.14 11.47 11.47 11.47 11.47-5.14 11.47-11.47-5.14-11.47-11.47-11.47m8.1 18.94c-1.28 1.39-3.08 1.82-5.21-.14-1.62-1.5-2.23-4.75-2.43-6.22-.14.06-.3.1-.47.1-.21 0-.41-.06-.59-.16-.79 1.36-2.48 3.93-4.44 4.67-2.71 1.03-4.22-.03-4.89-1.8-.68-1.77-.26-3.57 2.45-4.6 1.9-.73 4.76-.01 6.29.48.07-.38.31-.7.65-.87-.71-1.47-1.75-4.11-1.32-6.04.64-2.83 2.36-3.5 4.21-3.08s3.11 1.76 2.48 4.59c-.44 1.96-2.58 3.92-3.84 4.94.13.19.2.42.2.67 0 .13-.03.26-.07.38 1.48.07 4.78.41 6.4 1.91 2.13 1.96 1.85 3.79.57 5.18h.01Z"></path></g></svg>)
    },
    {
      label: "Wärmepumpe Integration",
      value: "hasWaermepumpeIntegration",
      active: false,
      svg: () => (<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 81.82 60.36"><g fill="currentColor"><path d="M48.5 26.7v27.7h-43V26.7zm5.5-5.51H0V59.9h54z"></path><path d="M32.99 30.9h12.16v3.25H32.99zM32.99 36.71h12.16v3.25H32.99zM32.99 42.51h12.16v3.25H32.99zM19.21 29.49c-6.34 0-11.47 5.14-11.47 11.47s5.14 11.47 11.47 11.47 11.47-5.14 11.47-11.47-5.14-11.47-11.47-11.47m8.1 18.94c-1.28 1.39-3.08 1.82-5.21-.14-1.62-1.5-2.23-4.75-2.43-6.22-.14.06-.3.1-.47.1-.21 0-.41-.06-.59-.16-.79 1.36-2.48 3.93-4.44 4.67-2.71 1.03-4.22-.03-4.89-1.8-.68-1.77-.26-3.57 2.45-4.6 1.9-.73 4.76-.01 6.29.48.07-.38.31-.7.65-.87-.71-1.47-1.75-4.11-1.32-6.04.64-2.83 2.36-3.5 4.21-3.08s3.11 1.76 2.48 4.59c-.44 1.96-2.58 3.92-3.84 4.94.13.19.2.42.2.67 0 .13-.03.26-.07.38 1.48.07 4.78.41 6.4 1.91 2.13 1.96 1.85 3.79.57 5.18h.01ZM55.63 37.95v-3h3.05c2.55 0 4.55 2.78 4.55 6.32v9.18c0 1.83 1.49 3.32 3.32 3.32h9.54v3h-9.54c-3.48 0-6.32-2.83-6.32-6.32v-9.18c0-2-.93-3.32-1.55-3.32z"></path><path d="M75.84 50.2a5.08 5.08 0 1 1-.002 10.162A5.08 5.08 0 0 1 75.84 50.2M55.63 28.99h21.36v3H55.63z"></path><path d="M76.74 35.57a5.08 5.08 0 1 0-.002-10.162 5.08 5.08 0 0 0 .002 10.162M55.63 22.41v3h3.05c2.55 0 4.55-2.78 4.55-6.32V9.91c0-1.83 1.49-3.32 3.32-3.32h9.54v-3h-9.54c-3.48 0-6.32 2.83-6.32 6.32v9.18c0 2-.93 3.32-1.55 3.32z"></path><path d="M75.84 10.16a5.08 5.08 0 1 0-.002-10.162 5.08 5.08 0 0 0 .002 10.162"></path></g></svg>)
    }
  ]);
  const [previousCategories, setPreviousCategories] = useState([]);
  const partnerMapClusters = useMemo(
    () => clusterMapItems(filteredLocations, mapZoom, getClusterGridSize(mapZoom), !hasUserPannedMap),
    [filteredLocations, mapZoom, hasUserPannedMap]
  );
  const eventMapClusters = useMemo(
    () => clusterMapItems(filteredEvents, mapZoom),
    [filteredEvents, mapZoom]
  );

  const handleMapClusterClick = (cluster) => {
    if (!map || cluster.members.length < 2) return;

    const currentZoom = map.getZoom() || mapZoom;
    let targetZoom = Math.min(currentZoom + 1, 16);

    // Jump directly to the first level where this node produces at least two
    // visible children. Children can be individual markers or smaller clusters.
    for (let zoom = targetZoom; zoom <= 16; zoom += 1) {
      targetZoom = zoom;
      if (clusterMapItems(cluster.members, zoom).length > 1) break;
    }

    setMapCenter(cluster.position);
    map.panTo(cluster.position);
    map.setZoom(targetZoom);
  };

  const handleMapClick = (event) => {
    if (!event.latLng) return;

    const clusters = searchResultType === SEARCH_RESULT_TYPE.EVENT
      ? eventMapClusters
      : partnerMapClusters;
    const clickPoint = projectMapPoint(event.latLng.toJSON(), mapZoom);
    let nearestCluster = null;
    let nearestDistance = Number.POSITIVE_INFINITY;

    clusters.forEach((cluster) => {
      if (cluster.members.length < 2) return;
      const clusterPoint = projectMapPoint(cluster.position, mapZoom);
      const distance = Math.hypot(clusterPoint.x - clickPoint.x, clusterPoint.y - clickPoint.y);
      if (distance < nearestDistance) {
        nearestCluster = cluster;
        nearestDistance = distance;
      }
    });

    if (nearestCluster && nearestDistance <= 36) {
      handleMapClusterClick(nearestCluster);
    }
  };

  // const [wantsEdsnInfo, setWantsEdsnInfo] = useState(false);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries
  });

  useEffect(() => {
    // Check if the shadow root is available in the window object
    const shadowRoot = window.e3dcMapShadowRoot;
    if (shadowRoot) {
      const handleSelectEvent = (e) => {
        const event = Object.values(events || {}).find((item) => String(item.id) === String(e.detail.id));
        if (!event) {
          console.error('Event not found');
          return;
        }

        if (hasLockedPartnerFilter && !lockedPartnerIdSet.has(getEventPartnerIdentifier(event))) {
          return;
        }

        setSelectedToggles({ showEvents: true });

        // selectedToogle sets setSelectedEvent(null) wait a little bit before set select event
        setTimeout(() => {
          setSelectedEvent(event);
          const searchLabel = `${event.zipcode} ${event.city}`;
          setSearchString(searchLabel);
          // Filled programmatically - must not open the suggestion list.
          suppressSuggestionsRef.current = searchLabel.trim();
          setSearchQuery(searchLabel);
          const newCenter = { lat: event.lat, lng: event.lng };
          map.panTo(newCenter);
          setMapCenter(newCenter);
          map.setZoom(9);
          filterLocations(newCenter);
          filterEvents(newCenter);
        }, 100);

      };
      shadowRoot.addEventListener('e3dcMapSelectEvent', handleSelectEvent);
      window.e3dcMapShadowRootInitialized = true;
      return () => {
        shadowRoot.removeEventListener('e3dcMapSelectEvent', handleSelectEvent);
      };
    }
  }, [events, map, hasLockedPartnerFilter, lockedPartnerIdSet]);

  useEffect(() => {
    setFilteredLocations(hasLockedPartnerFilter ? lockedPartnerLocations : locations);   
    setCategories(prev =>
      prev.map(cat =>
        cat.value === 'hasPvSpeicher'
          ? { ...cat, active: true }
          : cat
      )
    );

  }, [locations, hasLockedPartnerFilter, lockedPartnerLocations]);

  useEffect(() => {
    setFilteredEvents(hasLockedPartnerFilter ? lockedPartnerEvents : events);
  }, [events, hasLockedPartnerFilter, lockedPartnerEvents]);

  // Development only: makes gaps in the delivered data visible (see feedback 4 + 5).
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    if (!locations.length && !Object.values(events || {}).length) return;

    const allEvents = Object.values(events || {});
    const orphanEvents = allEvents.filter((event) => !locationsById[event.partnerId]);

    console.info('[e3dc-map] Data overview', {
      partnersTotal: locations.length,
      partnersWithGewerbe: locations.filter((location) => location.hasGewerbe).length,
      partnersWithPvSpeicher: locations.filter((location) => location.hasPvSpeicher).length,
      eventsTotal: allEvents.length,
      eventsWithoutPartner: orphanEvents.length,
      eventsWithoutPartnerIds: orphanEvents.map((event) => event.id),
      eventsWithPvSpeicherPartner: allEvents.filter((event) => locationsById[event.partnerId]?.hasPvSpeicher).length
    });
  }, [locations, events, locationsById]);

  useEffect(() => {
    if (!hasLockedPartnerFilter) return;

    setSelectedToggles([]);
    setSelectedEvent(null);
    setCategories(prev =>
      prev.map(cat => ({ ...cat, active: false }))
    );
  }, [hasLockedPartnerFilter]);

  // Clearing the input deliberately does NOT reset the search any more. The current
  // result stays visible until the user explicitly resets via the X button, so a new
  // address can simply be typed over the old one.

  // filter location by services and search
  const filterLocations = useCallback((newCenter, skipFilterByDistance = false) => {

    if (newCenter) {
      setCurrentCenter(newCenter);
    }

    if (hasLockedPartnerFilter) {
      const center = newCenter ? newCenter : currentCenter;
      const filtered = center
        ? lockedPartnerLocations.map(item => ({
            ...item,
            distance: (Math.round(haversineDistance(center.lat, center.lng, item.lat, item.lng) * 10) / 10).toFixed(1)
          }))
        : lockedPartnerLocations;

      setFilteredLocations(filtered);
      setShowResult(Boolean(center) && !skipFilterByDistance);
      return;
    }

    var filtered = locations;

    const isActive = (value) => categories.find(cat => cat.value === value)?.active;

    // filter by category
    if (     
      isActive('hasPvSpeicher') ||
      isActive('hasEMobility') ||
      isActive('hasSmartMeter') ||
      isActive('hasWaermepumpeInstall') ||
      isActive('hasWaermepumpeIntegration') ||
      searchResultType === SEARCH_RESULT_TYPE.SHOWROOM ||
      customerType === 'B2B'
    ) {
      filtered = filtered.filter((location) => {
        var filter = true;   
        // redundant: we disable all filter anyway
        
        if (searchResultType === SEARCH_RESULT_TYPE.SHOWROOM) {
          filter &= location.hasShowroom;
        }
        else if (customerType === 'B2B') {
          filter &= location.hasGewerbe;        
        } else {
          if (isActive('hasPvSpeicher')) {
            filter &= location.hasPvSpeicher;
          }        
          if (isActive('hasEMobility')) {
            filter &= location.hasEMobility;
          }                
          if (isActive('hasSmartMeter')) {
            filter &= location.hasSmartMeter;
          }        
          if (isActive('hasWaermepumpeInstall')) {
            filter &= location.hasWaermepumpeInstall;
          }        
          if (isActive('hasWaermepumpeIntegration')) {
            filter &= location.hasWaermepumpeIntegration;
          }
        }        
        
        return filter;
      });
    }

    // - calculate distance for locations
    // - sort by distance
    // - get shortest locations
    const center = newCenter ? newCenter : currentCenter;
    if (center && !skipFilterByDistance) {

      // get nearest 20
      var filteredTmp = filtered.map(item => {
        return {
          ...item,
          distance: (Math.round(haversineDistance(center.lat, center.lng, item.lat, item.lng) * 10) / 10).toFixed(1)
        };
      })
        .filter(item => parseFloat(item.distance) <= item.radius); // Respect max radius

      if (filteredTmp.length === 0) {
        // when no results where found filter again without radius check
        filtered = filtered.map(item => {
          return {
            ...item,
            distance: (Math.round(haversineDistance(center.lat, center.lng, item.lat, item.lng) * 10) / 10).toFixed(1)
          };
        })
      } else {
        filtered = filteredTmp;
      }

      filtered = filtered
        .sort((a, b) => parseFloat(a.distance) - parseFloat(b.distance))
        .slice(0, 10);

      setShowResult(true);
    } else {
      setShowResult(false);
    }

    setFilteredLocations(filtered);
  }, [locations, categories, currentCenter, searchResultType, customerType, hasLockedPartnerFilter, lockedPartnerLocations]);
  
  // @todo: set location directly in event
  const filterEvents = useCallback((newCenter, skipFilterByDistance = false) => {

    var filtered = hasLockedPartnerFilter ? lockedPartnerEvents : Object.values(events);

    const isActive = (value) => categories.find(cat => cat.value === value)?.active;   

    // filter by category
    if (     
      isActive('hasPvSpeicher') ||
      isActive('hasEMobility') ||
      isActive('hasSmartMeter') ||
      isActive('hasWaermepumpeInstall') ||
      isActive('hasWaermepumpeIntegration')
    ) {
      filtered = filtered.filter((event) => {
        const location = locationsById[event.partnerId];
        if (!location) {
          // An event without a partner must not exist. It is skipped deliberately, but
          // reported in development so the data defect becomes visible.
          if (process.env.NODE_ENV !== 'production') {
            console.warn(`[e3dc-map] Event ${event.id} skipped: partner ${event.partnerId} not found.`);
          }
          return false;
        }
        var filter = true;        
        if (isActive('hasPvSpeicher')) {
          filter &= location.hasPvSpeicher;
        }        
        if (isActive('hasEMobility')) {
          filter &= location.hasEMobility;
        }        
        if (isActive('hasSmartMeter')) {
          filter &= location.hasSmartMeter;
        }        
        if (isActive('hasWaermepumpeInstall')) {
          filter &= location.hasWaermepumpeInstall;
        }        
        if (isActive('hasWaermepumpeIntegration')) {
          filter &= location.hasWaermepumpeIntegration;
        }
        return filter;
      });
    }

    // - calculate distance for locations
    // - sort by distance
    // - get shortest locations
    const center = newCenter ? newCenter : currentCenter;
    if (center && !skipFilterByDistance) {

      // get nearest 20
      var filteredTmp = filtered.map(item => {
        return {
          ...item,
          distance: (Math.round(haversineDistance(center.lat, center.lng, item.lat, item.lng) * 10) / 10).toFixed(1)
        };
      })
        .filter(item => parseFloat(item.distance) <= item.radius); // Respect max radius

      if (filteredTmp.length === 0) {
        // when no results where found filter again without radius check
        filtered = filtered.map(item => {
          return {
            ...item,
            distance: (Math.round(haversineDistance(center.lat, center.lng, item.lat, item.lng) * 10) / 10).toFixed(1)
          };
        })
      } else {
        filtered = filteredTmp;
      }

      filtered = filtered
        .sort((a, b) => a.distance - b.distance) // Sort by distance in ascending order
        .slice(0, 20); // get 20

      // prioritize alliance
      filtered = filtered.sort((a, b) => {
        if (a.distance < b.distance && a.distance <= 3) return -1; // a ist 1km oder weniger entfernt
        if (b.distance < a.distance && b.distance <= 3) return 1; // b ist 1km oder weniger entfernt
        // if (a.partnerType === 2 && b.partnerType !== 2) return -1; // a ist Alliance, kommt zuerst
        // if (b.partnerType === 2 && a.partnerType !== 2) return 1;  // b ist Alliance, kommt zuerst
        return 0; // Normal & Premium behalten ihre Reihenfolge
      });

      // get first 10
      filtered = filtered.slice(0, 10);

      setShowResult(true);
    } else {
      setShowResult(false);
    }

    // console.log('filteredEvents', filtered);
    setFilteredEvents(filtered);
  }, [events, categories, currentCenter, locationsById, hasLockedPartnerFilter, lockedPartnerEvents]);

  // refilter when active categories or the customer type change
  useEffect(() => {

    // reset selectedEvent, selectedLocation
    setSelectedEvent(null);
    setSelectedLocation(null);

    filterLocations();
    filterEvents();

  }, [categories, locations, customerType]);

  // B2B ignores the category checkboxes entirely (it filters on `hasGewerbe`), so they are
  // disabled on entry and restored when the user switches back to B2C. Without the restore
  // the map stayed stuck on the B2B result set and no filter was preselected any more.
  useEffect(() => {
    const previousCustomerType = previousCustomerTypeRef.current;
    previousCustomerTypeRef.current = customerType;

    if (customerType === previousCustomerType) {
      return;
    }

    setSelectedEvent(null);
    setSelectedLocation(null);

    if (customerType === 'B2B') {
      setPreviousCategoriesB2B(categories);
      setCategories(prev => withAllCategoriesDisabled(prev));
      return;
    }

    // Only restore when actually coming back from B2B, so selecting a customer type for
    // the first time does not reset a selection the user just made.
    if (previousCustomerType !== 'B2B') {
      return;
    }

    setCategories(previousCategoriesB2B.length > 0
      ? previousCategoriesB2B
      : withDefaultCategoryState(categories));
    setPreviousCategoriesB2B([]);
  }, [customerType])

  // 
  useEffect(() => {

    // reset selectedEvent, selectedLocation
    setSelectedEvent(null); // problem in custom event
    setSelectedLocation(null);

    if (searchResultType === SEARCH_RESULT_TYPE.SHOWROOM) {
      // disable all categories
      setPreviousCategories(categories);
      setCategories(prev => withAllCategoriesDisabled(prev));
    } else {
      // In B2B the categories must stay disabled, otherwise leaving the showroom would
      // re-enable them behind the customer type's back.
      if (previousCategories.length > 0 && customerType !== 'B2B') {
        setCategories(previousCategories);
        setPreviousCategories([]);
      }
    }

    filterLocations();
    filterEvents();
  }, [searchResultType])

  useEffect(() => {
    if (selectedToggles.showEvents) {
      setSearchResultType(SEARCH_RESULT_TYPE.EVENT);
    }
    else if (selectedToggles.showShowroom) {      
      setSearchResultType(SEARCH_RESULT_TYPE.SHOWROOM);
    }
    else {      
      setSearchResultType(SEARCH_RESULT_TYPE.DEFAULT);
    }

  }, [selectedToggles]);

  function haversineDistance(lat1, lon1, lat2, lon2) {
    const r = 6371; // km
    const p = Math.PI / 180;
    const a = 0.5 - Math.cos((lat2 - lat1) * p) / 2
      + Math.cos(lat1 * p) * Math.cos(lat2 * p) *
      (1 - Math.cos((lon2 - lon1) * p)) / 2;
    return 2 * r * Math.asin(Math.sqrt(a));
  }

  // A search can be started at any time - also directly after a result - so the user no
  // longer has to reset the search first. After a failed lookup the button stays disabled
  // until the entry is changed (the error is cleared on every keystroke).
  const canSubmitSearch = Boolean(customerType)
    && searchQuery.trim().length > 2
    && !searchError;
  // The X only appears once there is actually something to reset - i.e. when a result
  // is shown. Before that the button offers geolocation.
  const showTrailingReset = showResult;

  const closeSuggestions = () => {
    // Bumping both counters invalidates prediction requests that are still in flight or
    // still pending in the debounce - otherwise their response would reopen the dropdown.
    suggestionSessionRef.current += 1;
    searchGenerationRef.current += 1;
    setShowSuggestions(false);
    setSearchSuggestions([]);
    setActiveSuggestionIndex(-1);
  };

  const handleSearch = (overrideAddress) => {
    const address = typeof overrideAddress === 'string' ? overrideAddress : searchQuery;

    // Selecting a suggestion always closes the dropdown - even when no search can run
    // yet because the customer type is still missing.
    closeSuggestions();

    if (customerType && address.trim().length > 2) {
      const geocoder = new window.google.maps.Geocoder();

      isSearchPendingRef.current = true;

      geocoder.geocode({ 
        address, 
        language: 'de',
        region: 'de', // bias only
        // componentRestrictions: { country: 'DE' }
      }, (results, status) => {
        if (status === window.google.maps.GeocoderStatus.OK) {

          const geoLocation = results[0].geometry.location
          const addressComponents = results[0].address_components
          const findComponent = (type) => addressComponents.find((address_component) => address_component.types.includes(type))
          const city = findComponent('locality')?.long_name || ''
          const zipcode = findComponent('postal_code')?.long_name || ''
          // Keep the entered zip code visible instead of replacing it with the city only.
          const name = [zipcode, city].filter(Boolean).join(' ') || address.trim()

          setSearchError('');
          setSearchString(name);
          // The normalised name must not reopen the dropdown.
          suppressSuggestionsRef.current = name.trim();
          isSearchPendingRef.current = false;
          setSearchQuery(name);
          setFilterCity(city);

          // Get the coordinates of the search result
          const newCenter = {
            lat: geoLocation.lat(),
            lng: geoLocation.lng(),
          };

          // Set the map to the new center and zoom in
          map.panTo(newCenter);
          setMapCenter(newCenter);
          map.setZoom(9);

          // Mark the searched address on the map for orientation
          setUserPosition(newCenter);

          // reset form
          setSelectedEvent(null);
          setSelectedLocation(null);

          // show/hide everything out of range
          filterLocations(newCenter);
          filterEvents(newCenter);
        } else {
          console.error('Geocode was not successful for the following reason: ' + status);
          // The input stays editable so a corrected entry can be submitted right away.
          isSearchPendingRef.current = false;
          setSearchError('Zu dieser Eingabe wurde kein Ort gefunden. Bitte prüfen Sie die PLZ oder den Ort.');
        }
      });
    }
  };

  // "(regions)" restricts the predictions to postal codes, cities and districts -
  // exactly what is wanted here, and no streets or businesses.
  useEffect(() => {
    const query = searchQuery.trim();

    // While a search is running the input is rewritten programmatically - never treat
    // that as user input.
    if (isSearchPendingRef.current) {
      return;
    }

    // Value based instead of a one-shot flag: if the value written back by the search
    // matches the one already applied, no request is made. A boolean could stay set and
    // would then swallow the next real keystroke.
    if (suppressSuggestionsRef.current === query) {
      suppressSuggestionsRef.current = null;
      return;
    }
    suppressSuggestionsRef.current = null;

    if (!isLoaded || query.length < 3) {
      setSearchSuggestions([]);
      setShowSuggestions(false);
      setActiveSuggestionIndex(-1);
      return;
    }

    const requestId = suggestionSessionRef.current + 1;
    suggestionSessionRef.current = requestId;
    const searchGeneration = searchGenerationRef.current;

    const timeoutId = setTimeout(() => {
      // A search was executed in the meantime - this request is obsolete.
      if (searchGeneration !== searchGenerationRef.current) return;

      const autocompleteService = new window.google.maps.places.AutocompleteService();

      autocompleteService.getPlacePredictions({
        input: query,
        language: 'de',
        componentRestrictions: { country: 'de' },
        types: ['(regions)']
      }, (predictions, status) => {
        // Ignore responses of superseded requests and of requests from before a search.
        if (requestId !== suggestionSessionRef.current) return;
        if (searchGeneration !== searchGenerationRef.current) return;

        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !predictions) {
          setSearchSuggestions([]);
          setShowSuggestions(false);
          return;
        }

        const seen = new Set();
        const suggestions = predictions
          .map((prediction) => {
            const mainText = prediction.structured_formatting?.main_text || prediction.description;
            // Including the country: "49080" + "Osnabrück, Deutschland"
            const secondaryText = prediction.structured_formatting?.secondary_text || '';
            // The input field itself stays free of the country: "49080 Osnabrück"
            const label = [mainText, secondaryText.replace(/,?\s*Deutschland$/, '')]
              .filter(Boolean)
              .join(' ');

            return {
              label,
              searchValue: prediction.description,
              mainText,
              secondaryText
            };
          })
          .filter((suggestion) => {
            if (!suggestion.label || seen.has(suggestion.label)) return false;
            seen.add(suggestion.label);
            return true;
          })
          .slice(0, 5);

        setSearchSuggestions(suggestions);
        setShowSuggestions(suggestions.length > 0);
        setActiveSuggestionIndex(-1);
      });
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, isLoaded]);

  const handleSuggestionSelect = (suggestion) => {
    // The selected value is set programmatically and must not produce new suggestions -
    // this also applies when no search runs yet because the customer type is missing.
    suppressSuggestionsRef.current = suggestion.label.trim();
    setSearchQuery(suggestion.label);
    // Geocode using the full description, it is the most unambiguous.
    handleSearch(suggestion.searchValue || suggestion.label);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'ArrowDown' && searchSuggestions.length > 0) {
      e.preventDefault();
      setShowSuggestions(true);
      setActiveSuggestionIndex((prev) => (prev + 1) % searchSuggestions.length);
      return;
    }

    if (e.key === 'ArrowUp' && searchSuggestions.length > 0) {
      e.preventDefault();
      setShowSuggestions(true);
      setActiveSuggestionIndex((prev) => prev <= 0 ? searchSuggestions.length - 1 : prev - 1);
      return;
    }

    if (e.key === 'Escape') {
      setShowSuggestions(false);
      setActiveSuggestionIndex(-1);
      return;
    }

    if (e.key === 'Enter') {
      const activeSuggestion = showSuggestions ? searchSuggestions[activeSuggestionIndex] : null;

      if (activeSuggestion) {
        handleSuggestionSelect(activeSuggestion);
        return;
      }

      if (canSubmitSearch) {
        handleSearch();
      }
    }
  };

  const handleCategoryMultiSelectChange = (category, active) => {    
    setCategories(prevCategories => 
        prevCategories.map(cat => 
            cat.value === category.value 
                ? { ...cat, active }
                : cat
        )
    );
  }

  const handleToggleBarChange = (selected) => {
    // console.log('handleToggleBarChange', selected)
    setSelectedToggles(selected);
  }

  // on enter:
  // - set map marker as active
  // - move to position
  const handleSearchResultItemEnter = (location, index) => {
    map.panTo({ lat: location.lat, lng: location.lng });
    setFilteredLocations((prevLocations) =>
      prevLocations.map((location, i) =>
        i === index
          ? { ...location, active: true }
          : location
      )
    );
  };

  // on leave: set map marker as inactive
  const handleSearchResultItemLeave = (location, index) => {
    setFilteredLocations((prevLocations) =>
      prevLocations.map((location, i) =>
        i === index
          ? { ...location, active: false }
          : location
      )
    );
  }

  // show on leave: set map marker as inactive
  const handleSearchResultItemClicked = (location, index) => {
    if (searchResultType !== SEARCH_RESULT_TYPE.SHOWROOM) {
      setSelectedLocation(location);
    }
  }

  const handleSearchResultEventItemEnter = (event, index) => {
    map.panTo({ lat: event.lat, lng: event.lng });
    setFilteredEvents((prevEvents) =>
      prevEvents.map((event, i) =>
        i === index
          ? { ...event, active: true }
          : event
      )
    );
  };

  // on leave: set map marker as inactive
  const handleSearchResultEventItemLeave = (event, index) => {
    setFilteredEvents((prevEvents) =>
      prevEvents.map((event, i) =>
        i === index
          ? { ...event, active: false }
          : event
      )
    );
  }

  // show on leave: set map marker as inactive
  const handleSearchResultEventItemClicked = (event, index) => {
    setSelectedEvent(event);
  }

  const resetMapResults = () => {
    setSearchQuery('');
    setSearchString('');
    setSearchError('');
    setFilterCity('');
    setShowResult(false);
    setCurrentCenter(null);
    setSelectedLocation(null);
    setSelectedEvent(null);
    setUserPosition(null);
    // The customer type is intentionally kept: the user should be able to search for
    // another address without picking B2C/B2B again.
    setSelectedToggles([]);
    setSearchResultType(SEARCH_RESULT_TYPE.DEFAULT);
    setPreviousCategories([]);
    setFilteredLocations(hasLockedPartnerFilter ? lockedPartnerLocations : locations);
    setFilteredEvents(hasLockedPartnerFilter ? lockedPartnerEvents : events);
    setHasUserPannedMap(false);

    if (map) {
      map.panTo(defaultCenter);
      setMapCenter(defaultCenter);
      map.setZoom(defaultZoom);
    }
  }

  const handleContactFormClose = () => {
    setSelectedLocation(null);
  }

  const handleMarkerClick = (location) => {

    const searchLabel = `${location.zipcode} ${location.city}`;
    setSearchString(searchLabel); // Set the city name
    // Filled programmatically - must not open the suggestion list.
    suppressSuggestionsRef.current = searchLabel.trim();
    closeSuggestions();
    setSearchQuery(searchLabel);

    // Set the map to the new center and zoom in
    const newCenter = { lat: location.lat, lng: location.lng };
    map.panTo(newCenter);
    setMapCenter(newCenter);
    map.setZoom(9);

    // Show/hide locations/events based on proximity
    filterLocations(newCenter);
    filterEvents(newCenter);
    setShowResult(true);

    // Set location
    setSelectedLocation(location);
  }

  const handleEventMarkerClick = (event) => {

    // Set event
    setSelectedEvent(event);
    // console.log('event', event);

    const searchLabel = `${event.zipcode} ${event.city}`;
    setSearchString(searchLabel); // Set the city name
    // Filled programmatically - must not open the suggestion list.
    suppressSuggestionsRef.current = searchLabel.trim();
    closeSuggestions();
    setSearchQuery(searchLabel);

    // Set the map to the new center and zoom in
    const newCenter = { lat: event.lat, lng: event.lng };
    map.panTo(newCenter);
    setMapCenter(newCenter);
    map.setZoom(9);

    // Show/hide locations/events based on proximity
    filterLocations(newCenter);
    filterEvents(newCenter);
    setShowResult(true);
  }

  const handleEventFormClose = () => {
    setSelectedEvent(null);
  }

  const handleSearchReset = () => {
    resetMapResults();
  }

  const handleSelectCustomerType = (type) => {
    setCustomerType(type);
  }

  const handleGeolocation = () => {
    if (!navigator.geolocation || isLocating) return;

    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const newCenter = { lat: latitude, lng: longitude };

        // Do not make map positioning dependent on reverse geocoding. Safari
        // can provide coordinates successfully even if the lookup takes longer.
        map?.panTo(newCenter);
        setMapCenter(newCenter);
        map?.setZoom(9);
        setSelectedEvent(null);
        setSelectedLocation(null);
        setCurrentCenter(null);
        setShowResult(false);
        setSearchError('');
        // Mark the determined position on the map for orientation
        setUserPosition(newCenter);

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: newCenter }, (results, status) => {
          if (status === window.google.maps.GeocoderStatus.OK && results[0]) {
            const addressComponents = results[0].address_components;
            const cityComponent = addressComponents.find((component) =>
              component.types.includes('locality') ||
              component.types.includes('postal_town') ||
              component.types.includes('administrative_area_level_3')
            );
            const city = cityComponent?.long_name || results[0].formatted_address;
            setSearchString(city);
            setSearchQuery(city);
            setFilterCity(cityComponent?.long_name || '');
          } else {
            console.error('Geocode was not successful for the following reason: ' + status);
          }
          setIsLocating(false);
        });
      },
      (error) => {
        setIsLocating(false);
        console.error('Location could not be determined:', error.message || error);
        window.alert(error.code === error.PERMISSION_DENIED
          ? 'Der Standortzugriff ist deaktiviert. Bitte erlauben Sie ihn in den Safari-Einstellungen für diese Website.'
          : 'Ihr Standort konnte nicht ermittelt werden. Bitte versuchen Sie es erneut oder geben Sie einen Ort ein.');
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000
      }
    );
  }

  const [configuratorLink, setConfiguratorLink] = useState(null);
  const [showScreenOverlay, setShowScreenOverlay] = useState(false);

  const handleContactFormScreenOverlayClose = () => {
    setShowScreenOverlay(false);
    setConfiguratorLink(null);
  }

  return (
    <div className='e3dcp map-container'>
      <div className='map-bg-gradient'></div>
      <div className='map-overlay-container'>
        {/* {(searchResultType === SEARCH_RESULT_TYPE.DEFAULT || searchResultType === SEARCH_RESULT_TYPE.SHOWROOM) && */}
        {(searchResultType === SEARCH_RESULT_TYPE.DEFAULT) &&  
          <>
          <ContactFormScreenOverlay      
              isVisible={showScreenOverlay}
              configuratorLink={configuratorLink}                        
              onClose={handleContactFormScreenOverlayClose}
          />       
          <ContactForm          
            location={selectedLocation}
            action={SUBMIT_ENDPOINT}
            onClose={handleContactFormClose}
            filterCity={filterCity}            
            categories={categories} 
            customerType={customerType} 
            setConfiguratorLink={setConfiguratorLink}
            setShowScreenOverlay={setShowScreenOverlay}        
          />      
          </>   
        }
        {searchResultType === SEARCH_RESULT_TYPE.EVENT &&
          <EventForm
            event={selectedEvent}
            action={EVENT_FORM_SUBMIT_ENDPOINT}
            onClose={handleEventFormClose}
            filterCity={filterCity}            
            categories={categories}
          />
        }
        {!showResult && (
          <div className="row">
            <div className='col-12'>
              <header className='map-panel-header'>
                <h2>Suche starten</h2>
                <p>Finden Sie Ihren Partner</p>
              </header>
            </div>
          </div>
        )}
        <div className="row">
          <div className='col-12'>            
            <div className={`map-search p-3 d-flex ${showResult ? 'without-title' : ''}`}>
              <button
                type="button"
                className='map-search-leading-icon'
                onClick={() => handleSearch()}
                disabled={!canSubmitSearch}
                aria-label="Suche starten">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10a7 7 0 1 0 14 0 7 7 0 0 0-14 0m18 11-6-6"></path></svg>
              </button>
              <input
                className={`map-search-input ${searchError ? 'has-error' : ''}`}
                type="text"
                aria-invalid={Boolean(searchError)}
                title={searchError || undefined}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  if (searchError) setSearchError('');
                }}
                onKeyDown={handleSearchKeyDown}
                onFocus={() => {
                  if (searchSuggestions.length > 0) setShowSuggestions(true);
                }}
                onBlur={() => {
                  // Delay, so a click on a suggestion is still registered.
                  setTimeout(() => setShowSuggestions(false), 150);
                }}
                role="combobox"
                aria-expanded={showSuggestions}
                aria-controls="map-search-suggestions"
                aria-autocomplete="list"
                autoComplete="off"
                placeholder="PLZ oder Ort"
              />
              {showSuggestions && searchSuggestions.length > 0 && (
                <ul className='map-search-suggestions' id="map-search-suggestions" role="listbox">
                  {searchSuggestions.map((suggestion, index) => (
                    <li key={suggestion.label} role="presentation">
                      <button
                        type="button"
                        role="option"
                        aria-selected={index === activeSuggestionIndex}
                        className={`map-search-suggestion ${index === activeSuggestionIndex ? 'active' : ''}`}
                        onMouseDown={(e) => e.preventDefault()}
                        onMouseEnter={() => setActiveSuggestionIndex(index)}
                        onClick={() => handleSuggestionSelect(suggestion)}>
                        <span className='map-search-suggestion-label'>{suggestion.mainText}</span>
                        {suggestion.secondaryText && (
                          <span className='map-search-suggestion-region'>{suggestion.secondaryText}</span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <button
                type="button"
                className={`map-search-button ${showTrailingReset ? 'reset' : 'location'}`}
                onClick={showTrailingReset ? handleSearchReset : handleGeolocation}
                disabled={isLocating}
                aria-label={showTrailingReset ? 'Suche zurücksetzen' : 'Meinen Standort verwenden'}>
                {showTrailingReset ? (
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="map-search-button-icon"><path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 6 6 18M6 6l12 12"></path></svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="map-search-button-icon"><path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m12 19 7-16-16 7 7 2 2 7Z"></path></svg>
                )}
              </button>
            </div>
          </div>
        </div>
        <div className="row">          
          <div className='col-12'>            
            {/* Checkboxes */}
            <CategoryMultiSelect
              categories={categories}
              onChange={handleCategoryMultiSelectChange}
              disabled={searchResultType === SEARCH_RESULT_TYPE.SHOWROOM || customerType === 'B2B'}
              customerType={customerType}
              onSelectCustomerType={handleSelectCustomerType}
              showCustomerTypeSelection={!showResult}
            />
          </div>
        </div>
        <div className="row">
          <div className='col-12'>
            {/* Toggles */}
            <ToggleBar
              toggles={toggles}
              selectedToggles={selectedToggles}
              onChange={handleToggleBarChange}
            />
          </div>
        </div>
        {/* Only on the start screen resp. after a reset. Once results are shown, a new
            search runs via Enter, the suggestion list or the magnifier icon. */}
        {!showResult && (
          <div className="row">
            <div className='col-12'>
              <div className='map-search-submit-container'>
                <button
                  className='map-search-submit'
                  onClick={() => handleSearch()}
                  disabled={!canSubmitSearch}>
                  Suchen
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="row">
          <div className='col-12'>
            {/* Search Result */}
            {((searchResultType === SEARCH_RESULT_TYPE.DEFAULT || searchResultType === SEARCH_RESULT_TYPE.SHOWROOM)) &&
              <SearchResult
                showResult={showResult}
                locations={filteredLocations}
                onSearchResultItemEnter={handleSearchResultItemEnter}
                onSearchResultItemLeave={handleSearchResultItemLeave}
                onSearchResultItemClicked={handleSearchResultItemClicked}
                selectedLocation={selectedLocation}
                customerType={customerType}
              />
            }
            {/* Search Result Event */}
            {searchResultType === SEARCH_RESULT_TYPE.EVENT &&
              <SearchResultEvent
                showResult={showResult}
                events={filteredEvents}
                onSearchResultItemEnter={handleSearchResultEventItemEnter}
                onSearchResultItemLeave={handleSearchResultEventItemLeave}
                onSearchResultItemClicked={handleSearchResultEventItemClicked}
                selectedEvent={selectedEvent}
              />
            }
          </div>
        </div>
        {/* Collapses the result list and resets the search - same behaviour as the X
            next to the search field. */}
        {showResult && (
          <button
            type="button"
            className='search-result-reset'
            onClick={handleSearchReset}
            aria-label="Ergebnisse schließen und Suche zurücksetzen"
            title="Ergebnisse schließen">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24">
              <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m18 15-6-6-6 6"></path>
            </svg>
          </button>
        )}
      </div>

      {isLoaded ? (
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          zoom={defaultZoom}
          center={mapCenter}
          onLoad={(map) => setMap(map)}
          onZoomChanged={() => {
            if (map) setMapZoom(map.getZoom() || defaultZoom);
          }}
          onDragStart={() => setHasUserPannedMap(true)}
          onClick={handleMapClick}
          options={{
            styles: MapStylesGreen,
            gestureHandling: 'cooperative',
            disableDefaultUI: true
          }}
        >
          {userPosition && (
            <MarkerF
              position={userPosition}
              icon={userPositionIcon()}
              // Not clickable, so partner markers underneath stay selectable even though
              // the orientation pin is drawn on top of them.
              clickable={false}
              zIndex={100000}
              title="Ihr Standort"
            />
          )}

          {(searchResultType === SEARCH_RESULT_TYPE.DEFAULT || searchResultType === SEARCH_RESULT_TYPE.SHOWROOM) && partnerMapClusters.map((cluster, index) => {
            if (cluster.members.length === 1) {
              const location = cluster.members[0];
              return (
                    <CustomMapMarker
                      key={`location-${location.id}`}
                      location={location}
                      onClick={handleMarkerClick}
                      allowTooltipShown
                    />
              );
            }

            return (
              <MapCluster
                key={`partner-cluster-${cluster.members.map((member) => member.id).sort().join('-')}`}
                cluster={cluster}
                kind="partner"
                onActivate={handleMapClusterClick}
              />
            );
          })}

          {searchResultType === SEARCH_RESULT_TYPE.EVENT && eventMapClusters.map((cluster, index) => {
            if (cluster.members.length === 1) {
              const event = cluster.members[0];
              return (
                    <EventMapMarker
                      key={`event-${event.id}`}
                      event={event}
                      onClick={handleEventMarkerClick}
                      allowTooltipShown
                    />
              );
            }

            return (
              <MapCluster
                key={`event-cluster-${cluster.members.map((member) => member.id).sort().join('-')}`}
                cluster={cluster}
                kind="event"
                onActivate={handleMapClusterClick}
              />
            );
          })}
        </GoogleMap>
      ) : (
        <div>Loading map...</div>
      )}
    </div>
  );
};

export default App;
